import client from 'core/axios';
import handleError from 'core/axios/handleError';
import { handleJSONResponse } from 'core/axios/handleResponse';
import RequestMethod from 'core/axios/RequestMethod';

/**
 * Retrieve a list of curricula
 * @param {string} version
 * @param {Object} params
 */
export const getCurriculumList = ({ params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/lesson-library/curricula/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Retrieve curriculum details
 * @param {number} curriculumId
 * @param {string} version
 */
export const getCurriculumDetails = ({ curriculumId, version = 'v1' }) =>
  client
    .request({
      url: `${version}/lesson-library/curricula/${curriculumId}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Retrieve a list of breakdown options related to the curriculum
 * @param {number} curriculumId
 * @param {string} version
 */
export const getBreakdownOptionList = ({
  curriculumId,
  params,
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/lesson-library/curricula/${curriculumId}/breakdown-options/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Retrieve breakdown lesson completion data for the given curriculum and option
 * @param {number} curriculumId
 * @param {Object} params - request query parameters
 * @param {number} params.lessonId - lesson ID
 * @param {('schools'|'grades'|'sections')} option
 * @param {string} version
 */
export const getBreakdownOptionData = ({
  curriculumId,
  option,
  params,
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/lesson-library/curricula/${curriculumId}/breakdown-options/${option}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Retrieve a list of lessons related to the curriculum
 * @param {number} curriculumId
 * @param {Object} params - request query parameters
 * @param {number} params.schools - used to filter lesson results by school
 * @param {number} params.grades - used to filter lesson results by grade
 * @param {number} params.caseloads - used to filter lesson results by section
 * @param {string} version
 */
export const getLessonList = ({ curriculumId, params = {}, version = 'v1' }) =>
  client
    .request({
      url: `${version}/lesson-library/curricula/${curriculumId}/lessons/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Retrieve lesson details
 * @param {number} curriculumId
 * @param {number} lessonId
 * @param {Object} params - request query parameters
 * @param {number} params.schools - used to filter lesson results by school
 * @param {number} params.grades - used to filter lesson results by grade
 * @param {number} params.caseloads - used to filter lesson results by section
 * @param {string} version
 */
export const getLessonDetails = ({
  curriculumId,
  lessonId,
  params = {},
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/lesson-library/curricula/${curriculumId}/lessons/${lessonId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Retrieve a list of tag categories and tags for each category
 * @param {string} version
 */
export const getTagCategoryList = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/lesson-library/tag-categories/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Retrieve a list of lesson experiences
 * @param {string} version
 */
export const getLessonExperienceList = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/lesson-library/lesson-experiences/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Retrieve a list of users with lesson results
 * @param {number} curriculumId
 * @param {number} lessonId
 * @param {Object} params - request parameters for filtering user collection
 * @param {string} params.caseloads - comma-separated caseload IDs
 * @param {string} params.grades - comma-separated grades
 * @param {boolean} params.hasNote - filters users for whom there is/there is not a note
 * @param {boolean} params.isCompleted - filters the users who completed/not completed the lesson
 * @param {number} params.limit - controls the maximum number of users that may be returned
 * @param {number} params.offset - controls the starting point within the collection of users
 * @param {string} params.ordering - controls the sorting of the user collection, possible values - last_name|completion_date, default - last_name
 * @param {string} params.schools - comma-separated school IDs
 * @param {string} params.search - filters users by their first and last name
 * @param {string} version
 */
export const getUserLessonResultList = ({
  curriculumId,
  lessonId,
  params = {},
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/lesson-library/curricula/${curriculumId}/lessons/${lessonId}/results/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Update lesson result for the given user
 * @param {number} curriculumId
 * @param {number} lessonId
 * @param {number} userId
 * @param {Object} payload - request data
 * @param {boolean} payload.isCompleted
 * @param {number} payload.experience
 * @param {string} payload.note
 * @param {string} version
 */
export const updateUserLessonResult = ({
  curriculumId,
  lessonId,
  userId,
  payload,
  studentScope,
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/lesson-library/curricula/${curriculumId}/lessons/${lessonId}/results/${userId}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params: {
        studentScope,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Update user lesson results in bulk
 * @param {number} curriculumId
 * @param {number} lessonId
 * @param {number} userId
 * @param {Object} payload - request data
 * @param {boolean} payload.isCompleted
 * @param {number} payload.experience
 * @param {string} payload.note
 * @param {Object} params - request parameters for filtering user collection
 * @param {string} params.caseloads - comma-separated caseload IDs
 * @param {string} params.excludeIds - comma-separated user IDs
 * @param {string} params.grades - comma-separated grades
 * @param {boolean} params.hasNote - filters users for whom there is/there is not a note
 * @param {string} params.ids - comma-separated user IDs
 * @param {boolean} params.isCompleted - filters the users who completed/not completed the lesson
 * @param {string} params.schools - comma-separated school IDs
 * @param {string} params.search - filters users by their first and last name
 * @param {string} version
 */
export const bulkUpdateUserLessonResult = ({
  curriculumId,
  lessonId,
  payload,
  params,
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/lesson-library/curricula/${curriculumId}/lessons/${lessonId}/results/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Export lessons results
 * @param {number} curriculumId
 * @param {number} lessonId
 * @param {Object} params - request parameters for filtering user collection
 * @param {string} params.caseloads - comma-separated caseload IDs
 * @param {string} params.grades - comma-separated grades
 * @param {boolean} params.hasNote - filters users for whom there is/there is not a note
 * @param {boolean} params.isCompleted - filters the users who completed/not completed the lesson
 * @param {number} params.limit - controls the maximum number of users that may be returned
 * @param {number} params.offset - controls the starting point within the collection of users
 * @param {string} params.ordering - controls the sorting of the user collection, possible values - last_name|completion_date, default - last_name
 * @param {string} params.schools - comma-separated school IDs
 * @param {string} params.search - filters users by their first and last name
 * @param {string} version
 */
export const exportLessonResults = ({
  curriculumId,
  lessonId,
  params,
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/lesson-library/curricula/${curriculumId}/lessons/${lessonId}/export/`,
      method: RequestMethod.POST,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);
